import React, { useState } from "react";
import {
  Heading,
  HeadingVariant,
  Text,
  TextVariant,
  TextWeight,
  View,
  Button,
  ButtonVariant,
  WalletIcon2,
  LightningBoltIcon,
  RocketIcon,
} from "@pufferfinance/puffer-ui-components";

import { X402Payment } from "./X402Payment";
import { AutomatedPurchasing } from "./AutomatedPurchasing";
import { useAppToast } from "../hooks/useAppToast";
import styles from "./DemoCards.module.scss";

interface DemoCardsProps {
  onBalanceRefresh: () => void;
}

export const DemoCards: React.FC<DemoCardsProps> = ({ onBalanceRefresh }) => {
  const { showInfo } = useAppToast();
  const [showX402Payment, setShowX402Payment] = useState(false);
  const [showAutomatedPurchasing, setShowAutomatedPurchasing] = useState(false);

  return (
    <View flexDirection="column">
      <div className={styles.grid}>
        {/* X402 Payment Demo Card */}
        <Button
          variant={ButtonVariant.Unstyled}
          onPress={() => setShowX402Payment(true)}
          className={styles.cardButton}
        >
          <View flexDirection="column" gap={4} className={styles.card}>
            <View className={styles.iconBoxPrimary}>
              <WalletIcon2 size={28} />
            </View>
            <Heading variant={HeadingVariant.H3} className={styles.cardTitle}>
              X402 Payment
            </Heading>
            <Text
              variant={TextVariant.Body2}
              color="var(--secondary-cadet-50)"
              className={styles.cardDesc}
            >
              Demonstrate API request payment via x402 protocol. A small amount
              of USDC will be deducted from your balance and paid to the
              specified merchant account.
            </Text>
            <Text
              variant={TextVariant.Body2}
              weight={TextWeight.SemiBold}
              className={styles.ctaPrimary}
            >
              Try Demo →
            </Text>
          </View>
        </Button>

        {/* Automated Purchasing Demo Card */}
        <Button
          variant={ButtonVariant.Unstyled}
          onPress={() => setShowAutomatedPurchasing(true)}
          className={styles.cardButton}
        >
          <View flexDirection="column" gap={4} className={styles.card}>
            <View className={styles.iconBoxSuccess}>
              <LightningBoltIcon size={28} />
            </View>
            <Heading variant={HeadingVariant.H3} className={styles.cardTitle}>
              Automated Purchasing
            </Heading>
            <Text
              variant={TextVariant.Body2}
              color="var(--secondary-cadet-50)"
              className={styles.cardDesc}
            >
              Set a bid amount for an item with fluctuating prices. Your agent
              will automatically complete the purchase when the price becomes
              favorable.
            </Text>
            <Text
              variant={TextVariant.Body2}
              weight={TextWeight.SemiBold}
              className={styles.ctaSuccess}
            >
              Try Demo →
            </Text>
          </View>
        </Button>

        {/* Placeholder Demo Card */}
        <Button
          variant={ButtonVariant.Unstyled}
          onPress={() => showInfo("Coming Soon!")}
          className={styles.cardButton}
        >
          <View flexDirection="column" gap={4} className={styles.cardMuted}>
            <View className={styles.iconBoxMuted}>
              <RocketIcon size={28} />
            </View>
            <Heading
              variant={HeadingVariant.H3}
              className={styles.cardTitleMuted}
            >
              Coming Soon
            </Heading>
            <Text
              variant={TextVariant.Body2}
              color="var(--neutral-40)"
              className={styles.cardDesc}
            >
              Additional demo applications will be available here. Stay tuned
              for more delegated agent payment use cases.
            </Text>
            <Text
              variant={TextVariant.Body2}
              weight={TextWeight.SemiBold}
              className={styles.ctaMuted}
            >
              Coming Soon →
            </Text>
          </View>
        </Button>
      </div>

      {/* X402 Payment Modal */}
      {showX402Payment && (
        <View className={styles.overlay}>
          <View className={styles.modalSm}>
            <Button
              variant={ButtonVariant.Unstyled}
              onPress={() => setShowX402Payment(false)}
              className={styles.modalClose}
              aria-label="Close"
            >
              ×
            </Button>
            <X402Payment onBalanceRefresh={onBalanceRefresh} />
          </View>
        </View>
      )}

      {/* Automated Purchasing Modal */}
      {showAutomatedPurchasing && (
        <View className={styles.overlay}>
          <View className={styles.modalLg}>
            <Button
              variant={ButtonVariant.Unstyled}
              onPress={() => setShowAutomatedPurchasing(false)}
              className={styles.modalClose}
              aria-label="Close"
            >
              ×
            </Button>
            <AutomatedPurchasing onBalanceRefresh={onBalanceRefresh} />
          </View>
        </View>
      )}
    </View>
  );
};
